/**
 * TickerStrip (redesigned)
 * Compact 3-column leaderboard: Top Agents · Top Tenants · Top Topics
 * Clean light card, no scrolling, professional layout.
 */

import { useMemo } from 'react';
import { Typography, theme } from 'antd';
import { RobotOutlined, BankOutlined } from '@ant-design/icons';
import type { AgentStat, TimeseriesPoint, TenantRanking } from '../../types/insights';
import { useLanguage } from '@/hooks/useLanguage';

const { Text } = Typography;

const PALETTE = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444', '#06b6d4'];

function formatNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

function computeDelta(timeseries: TimeseriesPoint[], id: string, key: 'chatbot_id' | 'tenant_id'): number | null {
  const pts = timeseries.filter(p => p[key] === id);
  if (pts.length < 2) return null;
  const sorted = [...pts].sort((a, b) => a.date.localeCompare(b.date));
  const mid = Math.floor(sorted.length / 2);
  const first = sorted.slice(0, mid).reduce((s, p) => s + p.conversations, 0);
  const second = sorted.slice(mid).reduce((s, p) => s + p.conversations, 0);
  if (first === 0 && second === 0) return null;
  if (first === 0 && second >= 5) return 999; // "New" only with meaningful volume
  if (first === 0) return null;
  return ((second - first) / first) * 100;
}

function DeltaChip({ pct }: { pct: number | null }) {
  const { t } = useLanguage();
  if (pct === null || (pct !== 999 && Math.abs(pct) < 1)) return null;
  if (pct === 999) return (
    <span style={{ fontSize: 10, fontWeight: 600, color: '#16a34a', whiteSpace: 'nowrap' }}>
      {t('insights.pulse.newBadge')}
    </span>
  );
  const up = pct >= 0;
  const display = Math.abs(pct) >= 10 ? Math.round(Math.abs(pct)) : Math.abs(pct).toFixed(1);
  return (
    <span style={{ fontSize: 10, fontWeight: 600, color: up ? '#16a34a' : '#dc2626', whiteSpace: 'nowrap' }}>
      {up ? '↑' : '↓'} {display}%
    </span>
  );
}

// ─── Column ────────────────────────────────────────────────────────────────────

interface ColumnProps {
  title: string;
  subtitle: string;
  valueLabel: string;
  showTrend?: boolean;
  rows: { key: string; icon: React.ReactNode; name: string; value: number; delta: number | null }[];
  accent: string;
  separator?: boolean;
}

function LeaderboardColumn({ title, subtitle, valueLabel, showTrend = true, rows, accent, separator }: ColumnProps) {
  const { t } = useLanguage();
  const { token } = theme.useToken();
  return (
    <div style={{
      flex: 1,
      minWidth: 0,
      paddingInline: 20,
      borderInlineEnd: separator ? `1px solid ${token.colorBorderSecondary}` : undefined,
    }}>
      {/* Column header */}
      <div style={{ marginBottom: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ width: 3, height: 14, borderRadius: 2, background: accent, flexShrink: 0 }} />
          <Text style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            {title}
          </Text>
        </div>
        <Text style={{ fontSize: 10, color: '#94a3b8', marginInlineStart: 9, display: 'block', marginTop: 2 }}>
          {subtitle}
        </Text>
      </div>

      {/* Rows */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {/* Column labels row */}
        {rows.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
            <div style={{ width: 14, flexShrink: 0 }} />
            <div style={{ flexShrink: 0, width: 13 }} />
            <Text style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600, flex: 1, minWidth: 0, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              {t('skills.name')}
            </Text>
            <Text style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600, flexShrink: 0, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              {valueLabel}
            </Text>
            {showTrend && (
              <div style={{ width: 52, flexShrink: 0, display: 'flex', justifyContent: 'flex-end' }}>
                <Text style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  {t('insights.pulse.trend')}
                </Text>
              </div>
            )}
          </div>
        )}
        {rows.map((row, idx) => (
          <div key={row.key} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {/* Rank */}
            <Text style={{ fontSize: 11, color: '#cbd5e1', fontWeight: 600, width: 14, flexShrink: 0, textAlign: 'end' }}>
              {idx + 1}
            </Text>
            {/* Icon */}
            <div style={{ flexShrink: 0, color: PALETTE[idx % PALETTE.length], fontSize: 13 }}>
              {row.icon}
            </div>
            {/* Name */}
            <Text
              style={{ fontSize: 12, fontWeight: 500, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              title={row.name}
            >
              {row.name}
            </Text>
            {/* Value */}
            <Text style={{ fontSize: 12, fontWeight: 700, color: '#475569', flexShrink: 0 }}>
              {formatNumber(row.value)}
            </Text>
            {/* Delta */}
            {showTrend && (
              <div style={{ width: 52, flexShrink: 0, display: 'flex', justifyContent: 'flex-end' }}>
                <DeltaChip pct={row.delta} />
              </div>
            )}
          </div>
        ))}
        {rows.length === 0 && (
          <Text style={{ fontSize: 12, color: '#94a3b8' }}>{t('common.noData')}</Text>
        )}
      </div>
    </div>
  );
}

// ─── Props & main component ───────────────────────────────────────────────────

export interface TickerStripProps {
  agentStats: AgentStat[];
  agentTimeseries: TimeseriesPoint[];
  tenantRankings: TenantRanking[];
  tenantTimeseries: TimeseriesPoint[];
  loading: boolean;
}

export function TickerStrip({
  agentStats,
  agentTimeseries,
  tenantRankings,
  tenantTimeseries,
  loading,
}: TickerStripProps) {
  const { t } = useLanguage();
  const { token } = theme.useToken();

  const agentRows = useMemo(() =>
    [...agentStats]
      .sort((a, b) => b.total_conversations - a.total_conversations)
      .slice(0, 5)
      .map(a => ({
        key: a.chatbot_id,
        icon: <RobotOutlined />,
        name: a.chatbot_name,
        value: a.total_conversations,
        delta: computeDelta(agentTimeseries, a.chatbot_id, 'chatbot_id'),
      })),
    [agentStats, agentTimeseries]
  );

  const tenantRows = useMemo(() =>
    [...tenantRankings]
      .sort((a, b) => b.total_conversations - a.total_conversations)
      .slice(0, 5)
      .map(t => ({
        key: t.tenant_id,
        icon: <BankOutlined />,
        name: t.tenant_name,
        value: t.total_conversations,
        delta: computeDelta(tenantTimeseries, t.tenant_id, 'tenant_id'),
      })),
    [tenantRankings, tenantTimeseries]
  );


  if (loading && agentStats.length === 0) {
    return (
      <div style={{
        background: token.colorBgContainer,
        border: `1px solid ${token.colorBorderSecondary}`,
        borderRadius: token.borderRadiusLG,
        padding: '16px 0',
        height: 120,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <Text style={{ color: token.colorTextTertiary, fontSize: 13 }}>{t('common.loading')}</Text>
      </div>
    );
  }

  return (
    <div style={{
      background: token.colorBgContainer,
      border: `1px solid ${token.colorBorderSecondary}`,
      borderRadius: token.borderRadiusLG,
      padding: '16px 0',
      display: 'flex',
      alignItems: 'flex-start',
    }}>
      <LeaderboardColumn
        title={t('insights.pulse.agents')}
        subtitle={t('insights.pulse.top5AgentsSub')}
        valueLabel={t('insights.agentStats.conversations')}
        rows={agentRows}
        accent="#3b82f6"
        separator
      />
      <LeaderboardColumn
        title={t('insights.pulse.tenants')}
        subtitle={t('insights.pulse.top5TenantsSub')}
        valueLabel={t('insights.agentStats.conversations')}
        rows={tenantRows}
        accent="#8b5cf6"
        separator
      />
    </div>
  );
}
